<template>
  <div class="collapsible-text">
    <div
      ref="textContentContainer"
      :style="contentStyles"
      class="collapsible-text-content"
    >
      <div ref="textContent" class="text-content">
        <div v-html="content" stlye="display: inline;" />
      </div>
    </div>
    <div v-if="showActions" :style="actionStyles" class="collapsible-actions">
      <button v-if="isExpanded" class="collapse-button" @click="collapse">
        {{ collapseCtaText }}
      </button>
      <button v-else class="expand-button" @click="expand">
        {{ expandCtaText }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { CSSProperties, Ref } from 'vue'
import { ref, computed, onMounted, onUpdated } from 'vue'

interface Props {
  collapseCtaText: string
  content: string
  ctaAlignment: 'center' | 'right'
  expandCtaText: string
  isExpanded: boolean
  maxLines: number
}

const props = withDefaults(defineProps<Props>(), {
  collapseCtaText: 'Read less',
  ctaAlignment: 'right',
  expandCtaText: 'Read more',
  isExpanded: false,
  maxLines: 2,
})

const isExpanded = ref(props.isExpanded)
const textContent = ref<HTMLElement | null>(null)
const textContentContainer = ref<HTMLElement | null>(null)
let isOverflowByHeight = ref(false)
let isOverflowByLines = ref(false)

const actionStyles = computed(
  (): CSSProperties => ({
    'text-align': props.ctaAlignment,
  })
)

const contentStyles = computed((): CSSProperties => {
  if (props.maxLines === 0) {
    return {
      display: isExpanded.value ? 'block' : 'none',
    }
  } else {
    return {
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      '-webkit-line-clamp': isExpanded.value ? 'unset' : props.maxLines,
    }
  }
})

const showActions = computed(
  (): Boolean | Ref<boolean> =>
    props.maxLines === 0 || isOverflowByLines || isOverflowByHeight
)

function checkOverflow() {
  const textContentElement = textContent.value
  const textContentContainerElement = textContentContainer.value

  if (textContentElement && textContentContainerElement) {
    const textContentHeight = textContentElement.clientHeight
    const textContentContainerHeight = textContentContainerElement.clientHeight

    isOverflowByLines.value =
      textContentElement?.clientHeight >
      textContentContainerElement?.clientHeight
    isOverflowByHeight.value = textContentHeight > textContentContainerHeight
  }
}

function collapse() {
  isExpanded.value = false
}

function expand() {
  isExpanded.value = true
}

onMounted(() => {
  checkOverflow()
})

onUpdated(() => {
  checkOverflow()
})
</script>

<style lang="scss" scoped>
.text-content {
  @extend .body-m;
}

.collapse-button,
.expand-button {
  @extend .body-m;

  background-color: unset;
  border: none;
  cursor: pointer;
  margin-bottom: 10px;
  margin-top: 10px;
  padding: 0;
  text-decoration: underline;

  &:focus {
    outline: 0;
  }
}
</style>
